<template>
  <hypto-popup :title="'Create Order'">
    <template #body>
      <div class="merge-fields">
        <md-field :class="{'input-error': $v.orderId.$error}">
          <label>Order ID</label>
          <md-input
            v-model="orderId"
            :ref="'OrderId'"
            @blur="$v.orderId.$touch()"
          />
        </md-field>
        <h6 class="error-message" v-if="$v.orderId.$error && !$v.orderId.required">Please enter Order ID</h6>
        <h6 class="error-message" v-if="$v.orderId.$error && !$v.orderId.minLength">Please enter Order ID of minimum 5 chars</h6>

        <md-field :class="{'input-error': $v.amount.$error}">
          <label>Amount</label>
          <md-input
            v-model="amount"
            :type="isDesktop() ? 'text' : 'number'"
            @keypress="allowAmountFormat($event, amount)"
            @blur="$v.amount.$touch()"
          />
        </md-field>
        <i class="amount">{{words}}</i>
        <h6 class="error-message" v-if="$v.amount.$error && !$v.amount.required">Please enter amount value</h6>
        <h6 class="error-message" v-else-if="$v.amount.$error && !$v.amount.amount">Please enter only numeric values (upto 2 decimals)</h6>

        <md-field :class="{'input-error': $v.mobileNum.$error, 'no-left-border': true}">
          <label>Customer Mobile</label>

          <div class="custom-field">
            <span class="input-prefix">
              <h6>+91</h6>
            </span>

            <md-input
              v-model="mobileNum"
              maxlength=10
              :type="'tel'"
              @keypress="allowOnlyNumbers($event)"
              @blur="$v.mobileNum.$touch()"
            />
          </div>
        </md-field>
        <h6 class="error-message" v-if="$v.mobileNum.$error && !$v.mobileNum.required">Please enter a mobile number</h6>
        <h6 class="error-message" v-if="$v.mobileNum.$error && (!$v.mobileNum.maxLength || !$v.mobileNum.minLength)">Please enter mobile number of 10 digits</h6>

        <md-field :class="{'input-error': $v.note.$error}">
          <label>Notes</label>
          <md-input
            v-model="note"
            @blur="$v.note.$touch()"
          />
        </md-field>
        <h6 class="error-message" v-if="$v.note.$error && !$v.note.minLength">Note should contain atleast 2 characters</h6>
        <h6 class="error-message" v-else-if="$v.note.$error && !$v.note.alphaNumericSpace">Note should contain only alphanumeric characters and space</h6>

        <md-field :class="{'input-error': $v.redirectUrl.$error}">
          <label>Redirect URL</label>
          <md-input
            v-model="redirectUrl"
            @blur="$v.redirectUrl.$touch()"
          />
        </md-field>
        <h6 class="error-message" v-if="$v.redirectUrl.$error">Please enter a valid url</h6>

        <date-time
          :label="'Expiry Time'"
          @updateDateTime="(val) => expiryTime = val"
          :customDateTime="expiryTime"
          :fieldError="$v.expiryTime.$error"
          @blur="$v.expiryTime.$touch()"
        />
        <h6 class="error-message" v-if="$v.expiryTime.$error && !$v.expiryTime.required">Please enter UPI ID</h6>
        <h6 class="error-message" v-if="$v.expiryTime.$error && !$v.expiryTime.validTime">Please select a time in future</h6>

        <md-field id="PaymentModes">
          <label>Payment Modes</label>
          <md-select v-model="paymentModes" @md-opened="setDropdownPosOffset('PaymentModes')" multiple>
            <md-option
              v-for="(pymt, index) in paymentModeOptions"
              :key="index"
              :value="pymt.value"
            >
              {{pymt.text}}
            </md-option>
          </md-select>
        </md-field>
      </div>
    </template>

    <template #footer>
      <h6 class="error-message" v-if="errorMsg">{{errorMsg}}</h6>
      <button class="primary-btn align-r" @click="create">Create</button>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import DateTime from '@/components/global/DateTime'

import { allowAmountFormat, allowOnlyNumbers, checkFormErrors, setDropdownPosOffset } from '@/utils/helpers'
import { required, minLength, maxLength, url } from 'vuelidate/lib/validators'
import { amount, alphaNumericSpace } from '@/utils/helpers/validators'
import ApiServices from '@/services/api/pgOrders'
import moment from 'moment'

import numToWords from '@/mixins/numToWords'
import listMixins from '@/mixins/listMixins'

const validTime = (time) => {
  return moment().isBefore(time)
}

export default {
  name: 'CreateOrder',
  components: { HyptoPopup, DateTime },
  mixins: [numToWords, listMixins],
  data () {
    return {
      orderId: null,
      note: null,
      expiryTime: moment().add(30, 'm').toDate(),
      redirectUrl: null,
      mobileNum: null,
      paymentModeOptions: [
        {
          text: 'Cards',
          value: 'card'
        },
        {
          text: 'Net Banking',
          value: 'netbanking'
        },
        {
          text: 'UPI',
          value: 'upi'
        }
      ],
      paymentModes: [
        'card',
        'netbanking',
        'upi'
      ],
      errorMsg: null
    }
  },
  validations: {
    orderId: {
      required,
      minLength: minLength(5)
    },
    amount: {
      required,
      amount
    },
    note: {
      alphaNumericSpace,
      minLength: minLength(2)
    },
    redirectUrl: {
      url
    },
    expiryTime: {
      required,
      validTime
    },
    mobileNum: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(10)
    }
  },
  watch: {
    amount () {
      this.convertWords()
    }
  },
  created () {
    this.watchCollection(['orderId', 'mobileNum', 'amount', 'note', 'redirectUrl', 'paymentModes'], () => {
      this.errorMsg = null
    })
  },
  mounted () {
    this.$refs.OrderId.$el.focus()
  },
  methods: {
    setDropdownPosOffset,
    allowAmountFormat,
    allowOnlyNumbers,
    checkFormErrors,
    watchCollection (arr, cb) {
      arr.forEach((val) => this.$watch(val, cb))
    },
    getParams () {
      return {
        reference_id: this.orderId,
        note: this.note,
        customer_phone: this.mobileNum,
        payment_modes: this.paymentModes,
        return_url: this.redirectUrl,
        expiry_time: moment(this.expiryTime).format('YYYY-MM-DD HH:mm:ss'),
        amount: parseFloat(this.amount)
      }
    },
    create () {
      this.$v.$touch()

      if (!this.$v.$error && !this.errorMsg) {
        const params = this.getParams()

        ApiServices.create(params).then(response => {
          if (response.success) {
            this.$emit('refreshList')
            this.successToastMsg(response.message)
          } else {
            this.errorToastMsg(response.message)
          }
        }).catch(error => {
          this.errorMsg = error.body.reason
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/customField.scss';

  .amount {
    color: $grey-light;
    display: block;
    text-align: left;
    font-size: 12px;
  }
</style>
